/**
 * Diagnostics layer for CodeMirror: gutter severity markers, a tinted line background and a hover
 * tooltip per line. Content arrives already localized from React; positions are mapped through
 * edits so markers stay on their line while the user types. The same layer draws a thin rule in the
 * paper margin of every row, shaped by its reading tone (ADR-013, decision 6): solid lichen when verified,
 * dotted honey when executed on trust (« ~ »), dashed honey when read by island and not confirmed (« ≈ »),
 * dash-dot amber when not read but with proposals (« ◇ »), hollow grey when not read (« ? »), with the
 * reason on hover: a count that was not verified never looks like a verified one.
 */
import { RangeSet, RangeSetBuilder, StateEffect, StateField, type Extension } from '@codemirror/state';
import { Decoration, EditorView, GutterMarker, gutter, hoverTooltip, type DecorationSet } from '@codemirror/view';
import type { Severity } from '../engine/types';
import { TONE_GLYPH, type RowTone } from '../lib/rowStatus';
import { severityIconSvg, yarnDotSvg } from '../ui/icons';

export interface LineDiagnosticItem {
  severity: Severity;
  code: string;
  title: string;
  /** Catalogue explanation of the code, in the UI language. */
  explanation: string | null;
  messages: string[];
  sizesLabel: string;
}

export interface LineDiagnostics {
  /** Document position of the start of the line (already mapped to the current document). */
  from: number;
  severity: Severity;
  items: LineDiagnosticItem[];
}

export const setLineDiagnostics = StateEffect.define<LineDiagnostics[]>();

/** A row for the selected size: the tone to mark and its localized reason ('' when there is nothing to say). */
export interface LineStatus {
  /** Document position of the start of the line (already mapped to the current document). */
  from: number;
  tone: RowTone;
  label: string;
}

export const setLineStatuses = StateEffect.define<LineStatus[]>();

class SeverityMarker extends GutterMarker {
  constructor(
    readonly severity: Severity,
    readonly label: string,
  ) {
    super();
  }
  override eq(other: GutterMarker): boolean {
    return other instanceof SeverityMarker && other.severity === this.severity && other.label === this.label;
  }
  override toDOM(): Node {
    const el = document.createElement('span');
    el.className = `cm-fl-marker cm-fl-marker-${this.severity}`;
    el.innerHTML = yarnDotSvg(this.severity, 12);
    el.title = this.label;
    return el;
  }
}

interface DiagState {
  lines: DecorationSet;
  markers: RangeSet<GutterMarker>;
}

const diagField = StateField.define<DiagState>({
  create: () => ({ lines: Decoration.none, markers: RangeSet.empty }),
  update(value, tr) {
    let next = value;
    if (tr.docChanged) next = { lines: value.lines.map(tr.changes), markers: value.markers.map(tr.changes) };
    for (const effect of tr.effects) {
      if (effect.is(setLineDiagnostics)) next = build(effect.value, tr.state.doc.length);
    }
    return next;
  },
  provide: (f) => EditorView.decorations.from(f, (v) => v.lines),
});

function build(entries: LineDiagnostics[], docLength: number): DiagState {
  const sorted = entries.filter((e) => e.from >= 0 && e.from <= docLength).sort((a, b) => a.from - b.from);
  const lines = new RangeSetBuilder<Decoration>();
  const markers = new RangeSetBuilder<GutterMarker>();
  let last = -1;
  for (const e of sorted) {
    if (e.from === last) continue; // one entry per line
    last = e.from;
    const label = e.items.map((i) => `${i.title} (${i.sizesLabel})`).join('\n');
    lines.add(e.from, e.from, Decoration.line({ class: `cm-fl-line cm-fl-line-${e.severity}`, diagnostics: e }));
    markers.add(e.from, e.from, new SeverityMarker(e.severity, label));
  }
  return { lines: lines.finish(), markers: markers.finish() };
}

/** Reading tone marks: a line decoration per row (class `cm-row-<tone>`: checked, unverified, island, proposals, unread). */
const statusField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(value, tr) {
    let next = value;
    if (tr.docChanged) next = value.map(tr.changes);
    for (const effect of tr.effects) {
      if (effect.is(setLineStatuses)) next = buildStatuses(effect.value, tr.state.doc.length);
    }
    return next;
  },
  provide: (f) => EditorView.decorations.from(f),
});

function buildStatuses(entries: LineStatus[], docLength: number): DecorationSet {
  const sorted = entries.filter((e) => e.from >= 0 && e.from <= docLength).sort((a, b) => a.from - b.from);
  const lines = new RangeSetBuilder<Decoration>();
  let last = -1;
  for (const e of sorted) {
    if (e.from === last) continue; // one entry per line
    last = e.from;
    lines.add(e.from, e.from, Decoration.line({ class: `cm-row-${e.tone}`, status: e }));
  }
  return lines.finish();
}

function diagnosticsAtLine(view: EditorView, lineFrom: number): LineDiagnostics | null {
  let found: LineDiagnostics | null = null;
  view.state.field(diagField).lines.between(lineFrom, lineFrom, (_from, _to, deco) => {
    found = (deco.spec as { diagnostics?: LineDiagnostics }).diagnostics ?? null;
    return false;
  });
  return found;
}

function statusAtLine(view: EditorView, lineFrom: number): LineStatus | null {
  let found: LineStatus | null = null;
  view.state.field(statusField).between(lineFrom, lineFrom, (_from, _to, deco) => {
    found = (deco.spec as { status?: LineStatus }).status ?? null;
    return false;
  });
  return found;
}

function tooltipDom(entry: LineDiagnostics | null, status: LineStatus | null): HTMLElement {
  const root = document.createElement('div');
  root.className = 'cm-fl-tooltip';
  root.setAttribute('role', 'tooltip');
  for (const item of entry?.items ?? []) {
    const block = document.createElement('div');
    block.className = `cm-fl-tip cm-fl-tip-${item.severity}`;
    const head = document.createElement('div');
    head.className = 'cm-fl-tip-head';
    const icon = document.createElement('span');
    icon.className = `cm-fl-tip-icon cm-fl-marker-${item.severity}`;
    icon.innerHTML = severityIconSvg(item.severity, 12);
    const title = document.createElement('strong');
    title.textContent = item.title;
    const sizes = document.createElement('span');
    sizes.className = 'cm-fl-tip-sizes';
    sizes.textContent = item.sizesLabel;
    head.append(icon, title, sizes);
    block.append(head);
    if (item.explanation) {
      const explanation = document.createElement('div');
      explanation.className = 'cm-fl-tip-explain';
      explanation.textContent = item.explanation;
      block.append(explanation);
    }
    for (const m of item.messages) {
      const p = document.createElement('div');
      p.className = 'cm-fl-tip-msg';
      p.textContent = m;
      block.append(p);
    }
    block.title = item.code;
    root.append(block);
  }
  if (status?.label) {
    const block = document.createElement('div');
    block.className = `cm-fl-tip cm-fl-tip-status cm-row-tip-${status.tone}`;
    const glyph = document.createElement('span');
    glyph.className = 'cm-fl-tip-glyph';
    glyph.setAttribute('aria-hidden', 'true');
    glyph.textContent = TONE_GLYPH[status.tone] || '✓';
    const text = document.createElement('span');
    text.textContent = status.label;
    block.append(glyph, text);
    root.append(block);
  }
  return root;
}

const diagnosticsTooltip = hoverTooltip(
  (view, pos) => {
    const line = view.state.doc.lineAt(pos);
    const entry = diagnosticsAtLine(view, line.from);
    const status = statusAtLine(view, line.from);
    // A row verified by a rule carries its green rule and nothing to say.
    if (!entry && !status?.label) return null;
    return { pos: line.from, end: line.to, above: true, create: () => ({ dom: tooltipDom(entry, status) }) };
  },
  { hoverTime: 250 },
);

const diagnosticsGutter = gutter({
  class: 'cm-fl-diag-gutter',
  markers: (view) => view.state.field(diagField).markers,
  initialSpacer: () => new SeverityMarker('info', ''),
});

export function diagnosticsExtension(): Extension {
  return [diagField, statusField, diagnosticsGutter, diagnosticsTooltip];
}
